
import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import GlobalViewModel from '@/state/GlobalViewModel';
import GlossRepository from '@/repositories/GlossRepository';
import { BehaviorSubject, combineLatest, merge, Subscription } from 'rxjs';
import Resource, { ResourceStatus } from '@/repositories/Resource';
import GlossSearchResult from '@/repositories/data/GlossSearchResult';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import Theme from '@/repositories/data/Theme';
import VariantCategory from '@/repositories/data/VariantCategory';
import SignShape from '@/repositories/data/SignShape';
import SignGesture from '@/repositories/data/SignGesture';
import SignOralComponent from '@/repositories/data/SignOralComponent';
import SignLocation from '@/repositories/data/SignLocation';
import SignPropertySelection from '@/repositories/data/SignPropertySelection';
import ReferenceDataRepository from '@/repositories/ReferenceDataRepository';
import { SortDirection } from '@/repositories/data/Pageable';
import SignApiPage from '@/repositories/data/SignApiPage';
import GeolocationFilter from '@/repositories/data/GeolocationFilter';
import SearchScreen from './SearchScreen.vue';
import GlossDetailsScreen from './GlossDetailsScreen.vue';
import { SignPropertyFilter, SearchContext } from './searchTypes';

function signPropertyComparer(a: SignPropertySelection, b: SignPropertySelection): boolean {
    return (
        a.shapeLeft === b.shapeLeft &&
        a.shapeRight === b.shapeRight &&
        a.gestureLeft === b.gestureLeft &&
        a.gestureRight === b.gestureRight &&
        a.location === b.location &&
        a.oralComponent === b.oralComponent
    );
}

function geolocationFilterComparer(a: GeolocationFilter, b: GeolocationFilter): boolean {
    return (
        a.minLatitude === b.minLatitude &&
        a.maxLatitude === b.maxLatitude &&
        a.minLongitude === b.minLongitude &&
        a.maxLongitude === b.maxLongitude &&
        a.mapLayers.length === b.mapLayers.length &&
        a.mapLayers.every((value, index) => value === b.mapLayers[index])
    );
}

@Component({
    components: {
        SearchScreen,
        GlossDetailsScreen,
    },
})
export default class SearchContainer extends Vue {
    public searchMode = 'words';
    public referenceDataLoading = true;
    public availableThemes: Theme[] = [];
    public availableVariantCategories: VariantCategory[] = [];
    public signShapes: SignShape[] = [];
    public signGestures: SignGesture[] = [];
    public signLocations: SignLocation[] = [];
    public signOralComponents: SignOralComponent[] = [];
    public themeIdSubject = new BehaviorSubject<string | null>(null);
    public variantCategoryIdSubject = new BehaviorSubject<string>('0');
    public signPropertySelectionSubject = new BehaviorSubject<SignPropertySelection>({
        shapeLeft: '',
        shapeRight: '',
        gestureLeft: '',
        gestureRight: '',
        location: '',
        oralComponent: '',
    });
    public geolocationFilterSubject = new BehaviorSubject<GeolocationFilter>({
        minLatitude: 0,
        maxLatitude: 0,
        minLongitude: 0,
        maxLongitude: 0,
        mapLayers: ['none'],
    });
    public currentPageSubject = new BehaviorSubject<number>(1);
    public orderingSubject = new BehaviorSubject<string>('name');
    public moduleSubject = new BehaviorSubject<string | null>(null);
    public searchResults: GlossSearchResult[] = [];
    public searchLoading: boolean = true;
    public searchTextQueryBasis: string = '';
    public searchResultsTotal: number = 0;
    public moduleName: string | null = null;
    public totalPageCount = 0;
    public activeSignPropertyFilters: SignPropertyFilter[] = [];
    // For cross-page navigation: push to first/last item of newly loaded page
    public pendingNavigation: 'first' | 'last' | null = null;

    private paginationSize = 25;
    private modeSubject = new BehaviorSubject<string>(this.searchMode);
    private searchSubject = new BehaviorSubject<string>('');
    private searchKeywordsSettingSubject = new BehaviorSubject<string>('starts_with');
    private searchSubscription = new Subscription();
    private referenceDataSubscription = new Subscription();
    private signPropertyFiltersSubscription = new Subscription();
    private routeQueryInfluencersSubscription = new Subscription();
    @Inject() private readonly globalViewModel!: GlobalViewModel;
    @Inject() private readonly glossRepository!: GlossRepository;
    @Inject() private readonly referenceDataRepository!: ReferenceDataRepository;

    // True when the current route is the gloss-details page
    get showingGlossDetails(): boolean {
        return this.$route.name === 'gloss-details';
    }

    // The gloss ID from route params when on gloss-details, otherwise null
    get currentGlossId(): string | null {
        return this.showingGlossDetails ? this.$route.params.id || null : null;
    }

    // Index of the current gloss within the loaded search results page (-1 if not found)
    get computedGlossIndex(): number {
        if (!this.currentGlossId) {
            return -1;
        } else {
            return this.searchResults.findIndex(r => r.id == this.currentGlossId);
        }
    }

    // True when navigated to gloss-details via SearchScreen (either via search params or the _fromSearch)
    get hasSearchNavigation(): boolean {
        const query = this.$route.query;
        if (query._fromSearch === '1') {
            return true;
        } else {
            return Object.keys(query).filter(key => key !== 'm').length > 0;
        }
    }

    get searchContext(): SearchContext | null {
        if (!this.showingGlossDetails || !this.hasSearchNavigation || this.computedGlossIndex < 0) {
            return null;
        }
        return {
            results: this.searchResults,
            currentIndex: this.computedGlossIndex,
            currentPage: this.currentPageSubject.value,
            totalPageCount: this.totalPageCount,
        };
    }

    // Current search query params
    get currentSearchQuery(): Record<string, string> {
        return { ...this.$route.query } as Record<string, string>;
    }

    public created(): void {
        this.updateFromRouteQuery(true);

        this.searchSubscription = combineLatest([
            this.globalViewModel.verifiedUser,
            this.currentPageSubject.pipe(distinctUntilChanged()),
            this.orderingSubject.pipe(distinctUntilChanged()),
            this.moduleSubject.pipe(distinctUntilChanged()),
            combineLatest([
                this.modeSubject,
                combineLatest([
                    this.searchSubject.pipe(distinctUntilChanged(), debounceTime(300)),
                    this.searchKeywordsSettingSubject,
                ]).pipe(
                    map(([search, searchKeywordSetting]) => ({
                        keyword: search,
                        anywhere: searchKeywordSetting !== 'starts_with',
                    })),
                ),
                this.signPropertySelectionSubject.pipe(distinctUntilChanged(signPropertyComparer), debounceTime(300)),
                this.geolocationFilterSubject.pipe(distinctUntilChanged(geolocationFilterComparer), debounceTime(300)),
                this.themeIdSubject.pipe(distinctUntilChanged()),
                this.variantCategoryIdSubject.pipe(distinctUntilChanged()),
            ]).pipe(
                map(([mode, keywordsFilter, signPropertyFilter, geolocationFilter, themeId, variantCategoryId]) => ({
                    ...(mode === 'words' && keywordsFilter),
                    ...(mode === 'parameters' && signPropertyFilter),
                    ...(mode === 'geolocation' && geolocationFilter),
                    themeId,
                    variantCategoryId,
                })),
            ),
        ])
            .pipe(
                switchMap(([user, currentPage, orderBy, moduleId, glossFilter]) =>
                    this.glossRepository.findGlossesByOptions({
                        ...glossFilter,
                        moduleId,
                        pageable: {
                            page: currentPage - 1,
                            size: this.paginationSize,
                            sort:
                                orderBy === 'name'
                                    ? [{ property: 'name', direction: SortDirection.ASC }]
                                    : [{ property: 'createdAt', direction: SortDirection.DESC }],
                        },
                    }),
                ),
            )
            .subscribe((glossPageResource: Resource<SignApiPage<GlossSearchResult>>) => {
                if (glossPageResource.status === ResourceStatus.SUCCESS) {
                    if (glossPageResource.data !== null) {
                        this.searchResults = glossPageResource.data.content;
                        this.searchResultsTotal = glossPageResource.data.totalElements;
                        this.totalPageCount = glossPageResource.data.totalPages;
                        this.$root.$emit('readyToScroll');
                        // Cross-page navigation: push to first/last gloss of the newly loaded page
                        if (this.pendingNavigation !== null && this.searchResults.length > 0) {
                            const gloss =
                                this.pendingNavigation === 'first'
                                    ? this.searchResults[0]
                                    : this.searchResults[this.searchResults.length - 1];
                            this.pendingNavigation = null;
                            this.pushToGloss(gloss.id);
                        }
                    } else {
                        this.searchResults = [];
                        this.searchResultsTotal = 0;
                    }
                }
                this.searchTextQueryBasis = this.searchSubject.getValue();
                this.searchLoading = glossPageResource.status === ResourceStatus.LOADING;
            });

        const referenceDataObservable = combineLatest([
            this.globalViewModel.verifiedUser,
            this.moduleSubject.pipe(distinctUntilChanged()),
        ]).pipe(switchMap(([user, moduleId]) => this.referenceDataRepository.findReferenceData(moduleId)));

        this.referenceDataSubscription = referenceDataObservable.subscribe(referenceData => {
            this.referenceDataLoading = referenceData.isLoading;
            if (referenceData.data !== null && referenceData.data.themes) {
                this.availableThemes = referenceData.data.themes;
            } else {
                this.availableThemes = [];
            }
            if (referenceData.data !== null && referenceData.data.variantCategories) {
                this.availableVariantCategories = referenceData.data.variantCategories;
            } else {
                this.availableVariantCategories = [];
            }
            if (referenceData.data !== null && referenceData.data.signShapes) {
                this.signShapes = referenceData.data.signShapes;
            } else {
                this.signShapes = [];
            }
            if (referenceData.data !== null && referenceData.data.signGestures) {
                this.signGestures = referenceData.data.signGestures;
            } else {
                this.signGestures = [];
            }
            if (referenceData.data !== null && referenceData.data.signLocations) {
                this.signLocations = referenceData.data.signLocations;
            } else {
                this.signLocations = [];
            }
            if (referenceData.data !== null && referenceData.data.signOralComponents) {
                this.signOralComponents = referenceData.data.signOralComponents;
            } else {
                this.signOralComponents = [];
            }
            if (referenceData.data !== null && referenceData.data.module && referenceData.data.module.name) {
                this.moduleName = referenceData.data.module.name;
            } else {
                this.moduleName = null;
            }
        });

        this.routeQueryInfluencersSubscription = merge(
            this.signPropertySelectionSubject.pipe(
                map(selection => {
                    const queryUpdate: any = {
                        sl: selection.shapeLeft.length > 0 ? selection.shapeLeft : null,
                        sr: selection.shapeRight.length > 0 ? selection.shapeRight : null,
                        gl: selection.gestureLeft.length > 0 ? selection.gestureLeft : null,
                        gr: selection.gestureRight.length > 0 ? selection.gestureRight : null,
                        lc: selection.location.length > 0 ? selection.location : null,
                        oc: selection.oralComponent.length > 0 ? selection.oralComponent : null,
                    };
                    return queryUpdate;
                }),
            ),
            this.themeIdSubject.pipe(
                map(id => {
                    const queryUpdate: any = { t: id };
                    return queryUpdate;
                }),
            ),
            this.variantCategoryIdSubject.pipe(
                map(id => {
                    const queryUpdate: any = { v: id !== '0' ? id : null };
                    return queryUpdate;
                }),
            ),
        ).subscribe(queryUpdate => {
            this.mergeRouteQuery(queryUpdate);
        });

        this.signPropertyFiltersSubscription = combineLatest([
            referenceDataObservable,
            this.signPropertySelectionSubject,
        ]).subscribe(([referenceData, selection]) => {
            const filters: SignPropertyFilter[] = [];
            if (selection.shapeLeft.length > 0) {
                filters.push({
                    type: 'shapeLeft',
                    id: selection.shapeLeft,
                    label:
                        referenceData.data !== null && referenceData.data.signShapes
                            ? referenceData.data.signShapes.find(entry => entry.id === selection.shapeLeft)!.labelNl!
                            : '',
                });
            }
            if (selection.shapeRight.length > 0) {
                filters.push({
                    type: 'shapeRight',
                    id: selection.shapeRight,
                    label:
                        referenceData.data !== null && referenceData.data.signShapes
                            ? referenceData.data.signShapes.find(entry => entry.id === selection.shapeRight)!.labelNl!
                            : '',
                });
            }
            if (selection.gestureLeft.length > 0) {
                filters.push({
                    type: 'gestureLeft',
                    id: selection.gestureLeft,
                    label:
                        referenceData.data !== null && referenceData.data.signGestures
                            ? referenceData.data.signGestures.find(entry => entry.id === selection.gestureLeft)!
                                  .labelNl!
                            : '',
                });
            }
            if (selection.gestureRight.length > 0) {
                filters.push({
                    type: 'gestureRight',
                    id: selection.gestureRight,
                    label:
                        referenceData.data !== null && referenceData.data.signGestures
                            ? referenceData.data.signGestures.find(entry => entry.id === selection.gestureRight)!
                                  .labelNl!
                            : '',
                });
            }
            if (selection.location.length > 0) {
                filters.push({
                    type: 'location',
                    id: selection.location,
                    label:
                        referenceData.data !== null && referenceData.data.signLocations
                            ? referenceData.data.signLocations.find(entry => entry.id === selection.location)!.labelNl!
                            : '',
                });
            }
            if (selection.oralComponent.length > 0) {
                filters.push({
                    type: 'oralComponent',
                    id: selection.oralComponent,
                    label:
                        referenceData.data !== null && referenceData.data.signOralComponents
                            ? referenceData.data.signOralComponents.find(entry => entry.id === selection.oralComponent)!
                                  .labelNl!
                            : '',
                });
            }
            this.activeSignPropertyFilters = filters;
        });
    }

    public beforeDestroy(): void {
        this.signPropertyFiltersSubscription.unsubscribe();
        this.routeQueryInfluencersSubscription.unsubscribe();
        this.referenceDataSubscription.unsubscribe();
        this.searchSubscription.unsubscribe();
    }

    private resetPage(): void {
        this.currentPageSubject.next(1);
        this.mergeRouteQuery({ p: null });
    }

    public onSearchTextQuery(query: string): void {
        this.searchSubject.next(query);
        this.resetPage();
        this.mergeRouteQuery({ q: query });
    }

    public onSearchKeywordSettingChanged(setting: string): void {
        this.searchKeywordsSettingSubject.next(setting);
    }

    public onNavigateToGloss(glossId: string, newIndex: number): void {
        const gloss = this.searchResults[newIndex];
        if (gloss) {
            this.pushToGloss(gloss.id);
        }
    }

    public onNavigateToNextPage(): void {
        if (this.currentPageSubject.value < this.totalPageCount) {
            this.pendingNavigation = 'first';
            this.onPageSelected(this.currentPageSubject.value + 1);
        }
    }

    public onNavigateToPrevPage(): void {
        if (this.currentPageSubject.value > 1) {
            this.pendingNavigation = 'last';
            this.onPageSelected(this.currentPageSubject.value - 1);
        }
    }

    // Navigate to a gloss-details route, preserving all current search query params + _fromSearch
    private pushToGloss(glossId: string): void {
        const categoryId =
            this.variantCategoryIdSubject.value !== '0' ? this.variantCategoryIdSubject.value : undefined;
        this.$router.push({
            name: 'gloss-details',
            params: { id: glossId, ...(categoryId ? { category_id: categoryId } : {}) },
            query: { ...this.$route.query, _fromSearch: '1' },
        });
    }

    public onChangeThemeSetting(themeId: string | null): void {
        if (themeId === this.themeIdSubject.value) {
            return;
        }
        this.themeIdSubject.next(themeId);
        this.resetPage();
    }

    public onChangeVariantCategorySetting(variantCategoryId: string): void {
        if (variantCategoryId === this.variantCategoryIdSubject.value) {
            return;
        }
        this.variantCategoryIdSubject.next(variantCategoryId);
        this.resetPage();
    }

    public onSelectWordTab(): void {
        this.searchMode = 'words';
    }

    public onSelectParametersTab(): void {
        this.searchMode = 'parameters';
    }

    public onSelectGeolocationTab(): void {
        this.searchMode = 'geolocation';
    }

    public onPageSelected(page: number): void {
        this.currentPageSubject.next(page);
        this.mergeRouteQuery({ p: page.toString(10) });
    }

    public onOrderByChanged(orderBy: string): void {
        this.orderingSubject.next(orderBy);
        this.mergeRouteQuery({ o: orderBy === 'createdAt' ? 'recent' : null });
    }

    @Watch('searchMode')
    public onSearchModeChanged(value: string): void {
        this.modeSubject.next(value);
        const routeValue = value === 'words' ? null : value;
        this.mergeRouteQuery({ m: routeValue });
    }

    public onSignPropertySelectionChanged(signPropertySelection: SignPropertySelection): void {
        if (signPropertyComparer(this.signPropertySelectionSubject.value, signPropertySelection)) {
            return;
        }
        this.signPropertySelectionSubject.next(signPropertySelection);
        this.resetPage();
    }

    public onGeolocationFilterChanged(filter: GeolocationFilter): void {
        if (geolocationFilterComparer(this.geolocationFilterSubject.value, filter)) {
            return;
        }
        this.geolocationFilterSubject.next(filter);
        this.resetPage();
    }

    public onClearFilter(type: string): void {
        const selection: SignPropertySelection = {
            ...this.signPropertySelectionSubject.value,
            [type]: '',
        };
        this.signPropertySelectionSubject.next(selection);
        this.resetPage();
    }

    public onClearFilters(): void {
        this.signPropertySelectionSubject.next({
            shapeLeft: '',
            shapeRight: '',
            gestureLeft: '',
            gestureRight: '',
            location: '',
            oralComponent: '',
        });
        this.resetPage();
    }

    @Watch('$route')
    public onRouteChanged(): void {
        this.updateFromRouteQuery(false);
    }

    private updateFromRouteQuery(initial: boolean): void {
        if (initial && this.$route.query.hasOwnProperty('q')) {
            this.searchSubject.next(this.$route.query.q as string);
            this.mergeRouteQuery({ q: this.$route.query.q as string });
        }
        if (this.$route.query.hasOwnProperty('m')) {
            const mode = this.$route.query.m;
            if (mode === 'parameters' || mode === 'geolocation') {
                this.searchMode = mode;
            } else {
                this.searchMode = 'words';
            }
        } else {
            this.searchMode = 'words';
        }
        if (this.$route.query.hasOwnProperty('o')) {
            this.orderingSubject.next(this.$route.query.o === 'recent' ? 'createdAt' : 'name');
        } else {
            this.orderingSubject.next('name');
        }
        if (this.$route.query.hasOwnProperty('l')) {
            this.moduleSubject.next(typeof this.$route.query.l === 'string' ? this.$route.query.l : null);
        } else {
            this.moduleSubject.next(null);
        }
        if (this.$route.query.hasOwnProperty('t')) {
            this.themeIdSubject.next(typeof this.$route.query.t === 'string' ? this.$route.query.t : null);
        } else {
            this.themeIdSubject.next(null);
        }
        if (this.$route.query.hasOwnProperty('v')) {
            this.variantCategoryIdSubject.next(typeof this.$route.query.v === 'string' ? this.$route.query.v : '0');
        } else {
            this.variantCategoryIdSubject.next('0');
        }
        if (this.$route.query.hasOwnProperty('p')) {
            this.currentPageSubject.next(
                typeof this.$route.query.p === 'string' && /^[-+]?(\d+)$/.test(this.$route.query.p)
                    ? parseInt(this.$route.query.p)
                    : 1,
            );
        } else {
            this.currentPageSubject.next(1);
        }
        const selection: SignPropertySelection = {
            shapeLeft: this.$route.query.hasOwnProperty('sl') ? '' + this.$route.query.sl : '',
            shapeRight: this.$route.query.hasOwnProperty('sr') ? '' + this.$route.query.sr : '',
            gestureLeft: this.$route.query.hasOwnProperty('gl') ? '' + this.$route.query.gl : '',
            gestureRight: this.$route.query.hasOwnProperty('gr') ? '' + this.$route.query.gr : '',
            location: this.$route.query.hasOwnProperty('lc') ? '' + this.$route.query.lc : '',
            oralComponent: this.$route.query.hasOwnProperty('oc') ? '' + this.$route.query.oc : '',
        };
        this.signPropertySelectionSubject.next(selection);
    }

    private mergeRouteQuery(query: any): void {
        const nextQuery = {
            ...this.$route.query,
            ...query,
        };
        Object.keys(query).forEach(key => {
            if (query[key] === null) {
                delete nextQuery[key];
            }
        });
        this.$router
            .replace({
                ...this.$router.currentRoute,
                name: this.$router.currentRoute.name!,
                query: nextQuery,
            })
            .catch(() => {
                // ignored
            });
    }
}
